/**
 * Pulling a saved design out of a backend row.
 *
 * Designs are read through the platform's generic row query (`getData` with
 * `attributesList: ['*']`), so what comes back is a TABLE ROW, and which column
 * holds the design is the backend's business: it may be the row itself, a JSON
 * column, or that column as a string.
 */

/**
 * The design inside one row, or null when the row holds none.
 *
 * Deliberately not keyed on a column name — a rename there would silently empty
 * the Templates list. The first value that looks like a design wins, and a
 * design is the only thing in a row that has `pages`.
 */
export function designOfRow(row: unknown): Record<string, unknown> | null {
  if (!row || typeof row !== 'object' || Array.isArray(row)) return null;
  const record = row as Record<string, unknown>;
  if (Array.isArray(record['pages'])) return record;

  for (const value of Object.values(record)) {
    const candidate = typeof value === 'string' ? parseJson(value) : value;
    if (isDesign(candidate)) return candidate;
  }
  return null;
}

function isDesign(value: unknown): value is Record<string, unknown> {
  return (
    !!value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    Array.isArray((value as Record<string, unknown>)['pages'])
  );
}

/** Only a string that could be an object is worth parsing — rows carry prose too. */
function parseJson(value: string): unknown {
  if (!value.trimStart().startsWith('{')) return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}
